// src/components/Admin/TaskManager/ExecutionHistoryTab.tsx

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useScheduledExecutions } from '../../../hooks/useScheduledExecutions';
import { EXECUTION_STATUS_OPTIONS, ExecutionStatus } from '../../../types/taskManager';
import { format } from 'date-fns';

const ExecutionHistoryTab: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<ExecutionStatus | ''>('');
  const [taskNameFilter, setTaskNameFilter] = useState<string>('');

  // Use the proper hook with filters
  const {
    executions,
    loading,
    error,
    refetch,
  } = useScheduledExecutions({
    status: statusFilter || undefined,
  });

  // Filter by task name (client-side since API doesn't support task_name filter)
  const filteredExecutions = useMemo(() => {
    if (!executions) return [];

    if (taskNameFilter) {
      return executions.filter(exec =>
        exec.scheduled_task_name === taskNameFilter
      );
    }

    return executions;
  }, [executions, taskNameFilter]);

  // Get unique task names for filter dropdown
  const uniqueTasks = useMemo(() => {
    if (!executions) return [];
    return Array.from(new Set(executions.map(e => e.scheduled_task_name))).sort();
  }, [executions]);

  const getStatusColor = (status: string) => {
    const option = EXECUTION_STATUS_OPTIONS.find(opt => opt.value === status);
    return option?.color || '#9e9e9e';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const formatDuration = (durationString?: string) => {
    if (!durationString) return 'N/A';

    // Duration format: "0:00:05.123456" or "HH:MM:SS.ffffff"
    const match = durationString.match(/(\d+):(\d+):(\d+(?:\.\d+)?)/);
    if (match) {
      const hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const seconds = parseFloat(match[3]);

      if (hours > 0) return `${hours}h ${minutes}m`;
      if (minutes > 0) return `${minutes}m ${seconds.toFixed(1)}s`;
      return `${seconds.toFixed(2)}s`;
    }
    return durationString;
  };

  const formatResult = (result: any) => {
    if (!result) return null;

    const parts: string[] = [];

    if (result.notifications_sent !== undefined) {
      parts.push(`✓ ${result.notifications_sent} sent`);
    }
    if (result.notifications_skipped !== undefined) {
      parts.push(`⊘ ${result.notifications_skipped} skipped`);
    }
    if (result.errors !== undefined && result.errors > 0) {
      parts.push(`✗ ${result.errors} errors`);
    }

    return parts.length > 0 ? parts.join(' | ') : 'Completed';
  };

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ExecutionStatus | '')}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          {EXECUTION_STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Task"
          value={taskNameFilter}
          onChange={(e) => setTaskNameFilter(e.target.value)}
          sx={{ minWidth: 200 }}
          disabled={loading}
        >
          <MenuItem value="">All Tasks</MenuItem>
          {uniqueTasks.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {filteredExecutions.length} execution{filteredExecutions.length !== 1 ? 's' : ''}
          </Typography>

          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} size="small" disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load execution history. Please try again.
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredExecutions.length === 0 ? (
        <Alert severity="info">
          {statusFilter || taskNameFilter
            ? 'No executions found matching the selected filters'
            : 'No execution history found'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Task Name</TableCell>
                <TableCell>Job ID</TableCell>
                <TableCell>Scheduled</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExecutions.map((exec) => (
                <TableRow
                  key={exec.id}
                  hover
                  sx={{
                    bgcolor: exec.status === 'failed'
                      ? 'rgba(244, 67, 54, 0.05)'
                      : 'inherit'
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {exec.scheduled_task_name}
                    </Typography>
                    {exec.scheduled_task && (
                      <Typography variant="caption" color="text.secondary">
                        ID: {exec.scheduled_task}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Tooltip title={exec.job_id}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          cursor: 'pointer',
                        }}
                      >
                        {exec.job_id.length > 35
                          ? `${exec.job_id.substring(0, 35)}...`
                          : exec.job_id}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(exec.scheduled_time)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(exec.started_at)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(exec.completed_at)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" fontWeight={500}>
                      {formatDuration(exec.execution_duration)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={exec.status}
                      size="small"
                      sx={{
                        bgcolor: getStatusColor(exec.status),
                        color: 'white',
                        fontSize: '0.7rem',
                        minWidth: 70,
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Box>
                      {exec.result && (
                        <Typography variant="caption" display="block">
                          {formatResult(exec.result)}
                        </Typography>
                      )}
                      {exec.error_message && (
                        <Tooltip title={exec.error_message} arrow>
                          <Chip
                            label="View Error"
                            size="small"
                            color="error"
                            sx={{ fontSize: '0.65rem', height: 18, mt: 0.5, cursor: 'pointer' }}
                          />
                        </Tooltip>
                      )}
                      {exec.job_execution_id && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Job Exec: {exec.job_execution_id}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ExecutionHistoryTab;